import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  listGenericMealPlans,
  type GenericMealPlanSummary,
  type MealPlanTemplateSummary,
} from '@/lib/api/mealPlans';
import type { ClientRestrictionProfile } from '@/utils/meal-plan-allergen-warnings';
import { extractErrorMessage } from '@/utils/toast-utils';
import trashBinTrashIcon from '@/assets/images/trash-bin-trash.svg';
import TemplatesSearchPopoverField from './TemplatesSearchPopoverField';
import { Tag } from '@/components/ui/tag';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

const ACCENT = '#20AEDC';
const GENERIC_PLAN_PAGE_SIZE = 100;

function readPaginationTotal(pagination: { total?: number; totalCount?: number; totalItems?: number; count?: number } | undefined): number | undefined {
  if (!pagination) return undefined;
  for (const value of [pagination.total, pagination.totalCount, pagination.totalItems, pagination.count]) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

export type NutritionalAnalysisActionsBarProps = {
  isExporting: boolean;
  isDeletingTemplate?: boolean;
  onSaveAsTemplate: () => void;
  /** Disables "Save as Template" (e.g. the meal plan has empty required slots). */
  saveAsTemplateDisabled?: boolean;
  /** Tooltip explaining why "Save as Template" is disabled. */
  saveAsTemplateDisabledReason?: string;
  onExport?: () => void;
  /** When true, Export PDF is visible but not clickable. */
  exportDisabled?: boolean;
  /** When false, hides full meal-plan export (e.g. draft plans). */
  showExport?: boolean;
  onAddMeal: () => void;
  onDeleteTemplate?: () => void;
  canManageMealSlots: boolean;
  showSaveAsTemplate?: boolean;
  showDeleteTemplate?: boolean;
  /** When false, hides "+ Add Meal" (e.g. embedded client generic meal plan). */
  showAddMeal?: boolean;
  /** Generic meal plan view: imports another generic meal plan into the current one. */
  showGenericMealPlanImport?: boolean;
  currentGenericPlanId?: string;
  genericMealPlanImportClientId?: string;
  onGenericMealPlanImport?: (plan: GenericMealPlanSummary) => void | Promise<void>;
  /** When set, shows import-template control with Add Meal and Export in one row (generic / template flows). */
  templatesSearchPlaceholder?: string;
  showTemplatesSearch?: boolean;
  onTemplatePick?: (template: MealPlanTemplateSummary) => void | Promise<void>;
  excludeImportedTemplateIds?: string[];
  restrictionClient?: ClientRestrictionProfile;
};

function getGenericPlanTitle(plan: GenericMealPlanSummary): string {
  return plan.name?.trim() || 'Untitled cohort meal plan';
}

function GenericMealPlanImportDialog({
  open,
  currentGenericPlanId,
  clientId,
  onClose,
  onImport,
}: {
  open: boolean;
  currentGenericPlanId?: string;
  clientId?: string;
  onClose: () => void;
  onImport?: (plan: GenericMealPlanSummary) => void | Promise<void>;
}) {
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [plans, setPlans] = React.useState<GenericMealPlanSummary[]>([]);
  const [loadedPlanCount, setLoadedPlanCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [fetchError, setFetchError] = React.useState('');
  const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => window.clearTimeout(t);
  }, [query]);

  React.useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFetchError('');
      try {
        const response = await listGenericMealPlans({
          clientId: clientId?.trim() || undefined,
          search: debouncedQuery || undefined,
          page: 1,
          pageSize: GENERIC_PLAN_PAGE_SIZE,
        });
        if (cancelled) return;
        if (!response.success) {
          throw new Error(response.message || 'Failed to load cohort meal plans');
        }
        const currentId = String(currentGenericPlanId ?? '').trim();
        const rawPlans = response.data?.genericPlans ?? [];
        setLoadedPlanCount(readPaginationTotal(response.data?.pagination) ?? rawPlans.length);
        setPlans(rawPlans.filter((plan) => String(plan.id).trim() !== currentId));
      } catch (err) {
        if (!cancelled) {
          setPlans([]);
          setLoadedPlanCount(0);
          setFetchError(extractErrorMessage(err) || 'Failed to load cohort meal plans');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [clientId, currentGenericPlanId, debouncedQuery, open]);

  React.useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebouncedQuery('');
    setSelectedPlanId(null);
    setLoadedPlanCount(0);
    setFetchError('');
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    setSelectedPlanId(null);
  }, [debouncedQuery, open]);

  const selectedPlan = React.useMemo(
    () => plans.find((plan) => String(plan.id) === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );
  const emptyStateCopy = React.useMemo(() => {
    if (debouncedQuery) {
      return {
        title: 'No matching cohort meal plans',
        message: 'Try a different search term or clear the search to browse available plans.',
      };
    }

    if (loadedPlanCount > 0) {
      return {
        title: 'No other cohort meal plans available',
        message: 'Create another cohort meal plan first, then return here to import it.',
      };
    }

    return {
      title: 'No cohort meal plans available yet',
      message: 'Create a cohort meal plan first, then return here to import it.',
    };
  }, [debouncedQuery, loadedPlanCount]);

  const handleImport = async () => {
    if (!selectedPlan || !onImport) return;
    setImporting(true);
    setFetchError('');
    try {
      await onImport(selectedPlan);
      onClose();
    } catch (err) {
      setFetchError(extractErrorMessage(err) || 'Unable to import cohort meal plan');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={importing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '16px',
            border: '1px solid #f3f4f6',
          },
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: '24px',
            pt: '20px',
            pb: '16px',
            borderBottom: '1px solid #F3F4F6',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: '42.5px' }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '14px',
                bgcolor: '#EAF7FC',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <DownloadRoundedIcon sx={{ fontSize: 18, color: ACCENT }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '18px',
                  fontWeight: 700,
                  lineHeight: '22.5px',
                  color: '#1E2939',
                }}
              >
                Import Cohort Meal Plan
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: '#1EA9D6',
                }}
              >
                Replaces all meals and slots in this cohort meal plan
              </Typography>
            </Box>
          </Stack>

          <IconButton
            type="button"
            onClick={onClose}
            disabled={importing}
            aria-label="Close"
            sx={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '999px',
              color: '#9CA3AF',
              p: '1px',
              alignSelf: 'flex-start',
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, px: 2, pb: 2 }}>
        <TextField
          inputRef={searchInputRef}
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search cohort meal plans..."
          disabled={importing}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontFamily: 'Inter, sans-serif',
              '& fieldset': { borderColor: '#e3e8ef' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            maxHeight: { xs: '50vh', sm: 386 },
            overflowY: 'auto',
            mx: -2,
            borderTop: '1px solid #f3f4f6',
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: ACCENT }} />
            </Box>
          ) : fetchError ? (
            <Typography sx={{ px: 2, py: 3, color: '#b42318', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
              {fetchError}
            </Typography>
          ) : plans.length === 0 ? (
            <Box
              sx={{
                minHeight: 136,
                px: 3,
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 0.75,
              }}
            >
              <Typography sx={{ color: '#344054', fontSize: '14px', fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                {emptyStateCopy.title}
              </Typography>
              <Typography sx={{ maxWidth: 410, color: '#667085', fontSize: '13px', lineHeight: 1.45, fontFamily: 'Inter, sans-serif' }}>
                {emptyStateCopy.message}
              </Typography>
            </Box>
          ) : (
            plans.map((plan) => {
              const selected = String(plan.id) === selectedPlanId;
              const tags = (plan.clinicalTags ?? []).filter(Boolean).slice(0, 3);
              return (
                <Box
                  key={plan.id}
                  component="button"
                  type="button"
                  onClick={() => setSelectedPlanId(String(plan.id))}
                  sx={{
                    width: '100%',
                    border: 'none',
                    bgcolor: selected ? '#ecfeff' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: '20px',
                    py: '12px',
                    '&:hover': { bgcolor: selected ? '#ecfeff' : '#fafafa' },
                  }}
                >
                  <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        minWidth: 0,
                        maxWidth: '100%',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '17.5px',
                        color: '#1e2939',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getGenericPlanTitle(plan)}
                    </Typography>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                      {tags.map((tag) => (
                        <Tag
                          key={tag}
                          variant="default"
                          size="sm"
                          className="bg-[#E6FAF8] px-2 py-[2.5px] font-[Inter,sans-serif] font-semibold leading-[16.5px] text-[#0D9488]"
                        >
                          {formatUnderscoreDisplayLabel(tag)}
                        </Tag>
                      ))}
                    </Stack>
                  </Stack>
                  <Typography
                    sx={{
                      flexShrink: 0,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '13px',
                      lineHeight: '20px',
                      color: ACCENT,
                    }}
                  >
                    {`${Number(plan.mealsCount ?? 0)} slots`}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>

        <DialogActions
          sx={{
            px: 0,
            pt: 1.25,
            mt: 1,
            borderTop: '1px solid #F3F4F6',
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={importing}
            sx={{
              textTransform: 'none',
              borderRadius: '14px',
              height: '37.5px',
              px: 2.2,
              color: '#374151',
              borderColor: '#E5E7EB',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleImport();
            }}
            variant="contained"
            disabled={!selectedPlan || importing}
            startIcon={importing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DownloadRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '14px',
              height: '35.5px',
              minWidth: 0,
              px: 2.2,
              bgcolor: selectedPlan && !importing ? '#1D2939' : '#9CA3AF',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: 600,
              '&.Mui-disabled': { bgcolor: '#9CA3AF', color: '#fff' },
              '&:hover': { bgcolor: selectedPlan && !importing ? '#101828' : '#7f8ea4' },
            }}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

const NutritionalAnalysisActionsBar: React.FC<NutritionalAnalysisActionsBarProps> = ({
  isExporting,
  isDeletingTemplate = false,
  onSaveAsTemplate,
  saveAsTemplateDisabled = false,
  saveAsTemplateDisabledReason,
  onExport,
  exportDisabled = false,
  showExport = true,
  onAddMeal,
  onDeleteTemplate,
  canManageMealSlots,
  showSaveAsTemplate = true,
  showDeleteTemplate = false,
  showAddMeal = true,
  showGenericMealPlanImport = false,
  currentGenericPlanId,
  genericMealPlanImportClientId,
  onGenericMealPlanImport,
  templatesSearchPlaceholder,
  showTemplatesSearch = true,
  onTemplatePick,
  excludeImportedTemplateIds,
  restrictionClient,
}) => {
  const [genericImportOpen, setGenericImportOpen] = React.useState(false);
  const genericTemplatesSearch =
    typeof templatesSearchPlaceholder === 'string' && templatesSearchPlaceholder.trim()
      ? templatesSearchPlaceholder
      : undefined;

  const isGenericTemplatePreviewBar = genericTemplatesSearch !== undefined;

  const saveAsTemplateButton = showSaveAsTemplate ? (
    <Tooltip
      title={saveAsTemplateDisabled && saveAsTemplateDisabledReason ? saveAsTemplateDisabledReason : ''}
      arrow
      placement="top"
      disableHoverListener={!saveAsTemplateDisabled || !saveAsTemplateDisabledReason}
    >
      {/* span keeps the tooltip working while the button is disabled */}
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Button
          variant="outlined"
          onClick={onSaveAsTemplate}
          disabled={saveAsTemplateDisabled}
          sx={{ borderColor: '#e3e8ef', color: '#475467', textTransform: 'none', fontSize: '0.8rem', minHeight: 34 }}
        >
          Save as Template
        </Button>
      </Box>
    </Tooltip>
  ) : null;

  const deleteTemplateButton = showDeleteTemplate ? (
    <Button
      variant="text"
      onClick={onDeleteTemplate}
      disabled={isDeletingTemplate}
      sx={{
        minWidth: 34,
        width: 34,
        px: 0,
        color: '#f87171',
        textTransform: 'none',
        minHeight: 34,
        border: 'none',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: 'transparent',
          color: '#dc2626',
        },
      }}
    >
      <Box component="img" src={trashBinTrashIcon} alt="" sx={{ width: 24, height: 24 }} />
    </Button>
  ) : null;

  const exportButtonLabel = isGenericTemplatePreviewBar ? 'Export PDF' : 'Export';

  const exportButton = showExport ? (
    <Button
      variant="outlined"
      onClick={onExport}
      disabled={isExporting || exportDisabled || !onExport}
      sx={{
        borderColor: isExporting ? '#20AEDC' : '#e3e8ef',
        bgcolor: isExporting ? '#EAF7FC' : '#fff',
        color: isExporting ? '#127EA5' : '#475467',
        textTransform: 'none',
        fontSize: '0.8rem',
        minWidth: isGenericTemplatePreviewBar ? 112 : 92,
        minHeight: 34,
        '&:hover': {
          borderColor: '#20AEDC',
          bgcolor: '#EAF7FC',
        },
        '&.Mui-disabled': isExporting
          ? {
              borderColor: '#20AEDC',
              bgcolor: '#EAF7FC',
              color: '#127EA5',
            }
          : {
              borderColor: '#e3e8ef',
              bgcolor: '#f8fafc',
              color: '#98a2b3',
            },
      }}
    >
      {isExporting ? 'Exporting...' : exportButtonLabel}
    </Button>
  ) : null;

  const addMealButton = showAddMeal ? (
    <Button
      data-demo-id="nutrition-add-meal"
      variant="contained"
      startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
      onClick={onAddMeal}
      disabled={!canManageMealSlots}
      sx={{
        bgcolor: '#20AEDC',
        color: '#ffffff',
        textTransform: 'none',
        fontSize: '0.8rem',
        minHeight: 34,
        px: 1.3,
        '&:hover': { bgcolor: '#20AEDC' },
        '&.Mui-disabled': {
          bgcolor: '#b9dbe7',
          color: '#ffffff',
        },
      }}
    >
      Add Meal
    </Button>
  ) : null;

  const importGenericMealPlanButton = showGenericMealPlanImport ? (
    <Button
      type="button"
      variant="outlined"
      onClick={() => setGenericImportOpen(true)}
      disabled={!onGenericMealPlanImport}
      sx={{
        borderColor: '#e3e8ef',
        color: '#475467',
        textTransform: 'none',
        fontSize: '0.8rem',
        minHeight: 34,
        px: 1.3,
        '&:hover': {
          borderColor: '#20AEDC',
          bgcolor: '#EAF7FC',
        },
      }}
    >
      Import from Cohort Meal Plan
    </Button>
  ) : null;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        sx={{ mb: 2, gap: 1, width: '100%' }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" justifyContent="flex-end">
          {addMealButton}
          {importGenericMealPlanButton}
          {isGenericTemplatePreviewBar && showTemplatesSearch ? (
            <TemplatesSearchPopoverField
              placeholder={genericTemplatesSearch}
              onTemplatePick={onTemplatePick}
              importOnSelect={Boolean(onTemplatePick)}
              excludeImportedTemplateIds={excludeImportedTemplateIds}
              restrictionClient={restrictionClient}
            />
          ) : null}
          {saveAsTemplateButton}
          {exportButton}
          {deleteTemplateButton}
        </Stack>
      </Stack>
      <GenericMealPlanImportDialog
        open={genericImportOpen}
        currentGenericPlanId={currentGenericPlanId}
        clientId={genericMealPlanImportClientId}
        onClose={() => setGenericImportOpen(false)}
        onImport={onGenericMealPlanImport}
      />
    </>
  );
};

export default NutritionalAnalysisActionsBar;
